import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCuentasBancoBC } from '@/hooks/useCuentasBancoBC';
import { CaretDown } from '@phosphor-icons/react';
import { useState } from 'react';

interface Props {
  /** Número de cuenta bancaria seleccionado (BC), o null si no se eligió. */
  value: string | null;
  onChange: (numero: string | null) => void;
  className?: string;
  /** Si se pasa, solo se listan cuentas de esa moneda (evita asignar una cuenta en otra moneda a una factura). */
  soloMoneda?: string;
}

/** Selector de una cuenta bancaria de Business Central (API v2.0, /bankAccounts). */
export function BankAccountPickerBC({ value, onChange, className, soloMoneda }: Props) {
  const { data, isLoading } = useCuentasBancoBC();
  const [abierto, setAbierto] = useState(false);

  const todasLasCuentas = data?.data ?? [];
  const cuentas = soloMoneda
    ? todasLasCuentas.filter((c) => (c.currencyCode || 'CRC') === soloMoneda)
    : todasLasCuentas;
  const seleccionada = todasLasCuentas.find((c) => c.number === value);

  return (
    // modal: ver comentario en VendorPickerBC — evita que el Dialog contenedor le
    // robe el foco al Popover (y con él, el scroll de la lista) en cada interacción.
    <Popover open={abierto} onOpenChange={setAbierto} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={className ?? 'w-full justify-between font-normal'}
        >
          <span className="truncate">
            {isLoading
              ? 'Cargando cuentas...'
              : seleccionada
                ? `${seleccionada.displayName} (${seleccionada.number})`
                : 'Elegir cuenta bancaria'}
          </span>
          <CaretDown className="h-4 w-4 shrink-0 opacity-50" weight="bold" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-2" align="start">
        <div className="max-h-64 space-y-0.5 overflow-auto">
          {cuentas.length === 0 ? (
            <p className="px-2 py-1.5 text-sm text-muted-foreground">
              {isLoading ? 'Cargando…' : 'Sin cuentas disponibles'}
            </p>
          ) : (
            cuentas.map((c) => (
              <button
                key={c.number}
                type="button"
                onClick={() => {
                  onChange(c.number);
                  setAbierto(false);
                }}
                className="flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="truncate font-medium">{c.displayName}</span>
                <span className="text-xs text-muted-foreground">
                  {c.number}
                  {c.currencyCode ? ` · ${c.currencyCode}` : ' · CRC'}
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
